import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, MapPin, Truck, Weight, Route, FileText, Phone, BadgeCheck, Repeat } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { StatusPill } from "@/components/common";
import { LoadCard } from "@/components/LoadCard";
import { AIDispatchPanel } from "@/components/AIDispatchPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function LoadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [load, setLoad] = useState(null);
  const [backhaul, setBackhaul] = useState([]);
  const [bidRate, setBidRate] = useState("");
  const [bidNotes, setBidNotes] = useState("");
  const [bidOpen, setBidOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load_ = async () => {
    try {
      const { data } = await api.get(`/loads/${id}`);
      setLoad(data);
      setBidRate(String(data.total_pay));
      const bh = await api.get(`/loads/${id}/backhaul`);
      setBackhaul(bh.data);
    } catch { toast.error("Load not found"); }
  };
  useEffect(() => { load_(); }, [id]);

  const requireCarrier = () => {
    if (!user) { toast.info("Log in as a carrier to continue"); nav("/login"); return false; }
    if (user.role !== "carrier") { toast.error("Only carriers can do that"); return false; }
    return true;
  };

  const book = async () => {
    if (!requireCarrier()) return;
    setBusy(true);
    try {
      await api.post(`/loads/${id}/book`);
      toast.success("Load booked!");
      load_();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not book"); }
    finally { setBusy(false); }
  };

  const submitBid = async () => {
    if (!requireCarrier()) return;
    setBusy(true);
    try {
      await api.post(`/loads/${id}/bid`, { rate: parseFloat(bidRate), notes: bidNotes });
      toast.success("Bid submitted to XD");
      setBidOpen(false);
      load_();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not bid"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!requireCarrier()) return;
    try { const { data } = await api.post(`/loads/${id}/save`); toast.success(data.saved ? "Saved" : "Removed"); } catch (e) { toast.error("Error"); }
  };

  if (!load) return <PublicLayout><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 py-12">
        <Link to="/find-loads" className="inline-flex items-center gap-2 text-sm text-xd-muted hover:text-white"><ArrowLeft size={16} /> Back to loads</Link>

        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-xd-paper border border-xd-border rounded-xl p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-xd-dim">{load.xd_number}</span>
                    {load.xd_exclusive && <span className="text-[10px] font-bold uppercase text-xd-orange bg-xd-orange/10 border border-xd-orange/30 rounded px-1.5 py-0.5">XD Exclusive</span>}
                  </div>
                  <h1 className="mt-2 font-heading text-3xl font-black tracking-tight">{load.origin_city}, {load.origin_state} → {load.dest_city}, {load.dest_state}</h1>
                </div>
                <StatusPill status={load.status} />
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-5">
                <Detail icon={MapPin} label="Pickup" value={load.pickup_date} />
                <Detail icon={MapPin} label="Delivery" value={load.delivery_date} />
                <Detail icon={Truck} label="Trailer" value={`${load.trailer_type} · ${load.trailer_length}ft`} />
                <Detail icon={Weight} label="Weight" value={`${load.weight?.toLocaleString()} lbs`} />
                <Detail icon={Route} label="Loaded / Deadhead" value={`${load.loaded_miles} mi / ${load.deadhead_miles} mi`} />
                <Detail icon={Route} label="Commodity" value={load.commodity} />
                <Detail icon={FileText} label="Dimensions" value={load.dimensions || "—"} />
                <Detail icon={Truck} label="Load Source" value={load.load_source} />
                <Detail icon={BadgeCheck} label="Shipper" value={load.broker_shipper} />
              </div>

              <div className="mt-6 pt-6 border-t border-xd-border">
                <p className="text-xs uppercase tracking-wide text-xd-dim">Special instructions</p>
                <p className="mt-1 text-sm text-xd-muted">{load.special_instructions || "None"}</p>
              </div>
            </div>

            {/* Status history */}
            <div className="bg-xd-paper border border-xd-border rounded-xl p-6">
              <h2 className="font-heading text-lg font-bold">Status History <span className="text-xs text-xd-dim font-normal">(audit trail)</span></h2>
              <ol className="mt-4 space-y-3">
                {load.status_history?.map((h) => (
                  <li key={h.id} className="flex items-start gap-3 text-sm">
                    <span className="mt-1 w-2 h-2 rounded-full bg-xd-orange shrink-0" />
                    <div>
                      <p className="text-white font-medium">{h.from_status ? `${h.from_status} → ` : ""}{h.to_status}</p>
                      <p className="text-xs text-xd-dim">{new Date(h.created_at).toLocaleString()} · {h.actor_name} {h.note && `· ${h.note}`}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Backhaul */}
            {backhaul.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Repeat size={18} className="text-xd-blue" /> Backhaul Opportunities</h2>
                <p className="text-sm text-xd-muted">Return loads near {load.dest_city}</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {backhaul.slice(0, 4).map((b) => <LoadCard key={b.id} load={{ ...b, match_score: b.backhaul_score }} />)}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar / actions */}
          <div className="space-y-4">
            <div className="bg-xd-paper border border-xd-border rounded-xl p-6 sticky top-24">
              <p className="text-xs uppercase tracking-wide text-xd-dim">Total Pay</p>
              <p className="font-heading text-4xl font-black">${load.total_pay?.toLocaleString()}</p>
              <p className="text-sm text-xd-blue font-semibold">${load.rate_per_mile}/mi · {load.total_miles} total mi</p>

              <div className="mt-6 space-y-3">
                <Button data-testid="book-load-btn" disabled={busy || load.status !== "Available"} onClick={book} className="w-full h-12 bg-xd-orange hover:bg-xd-orangeH text-white font-bold">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : load.status === "Available" ? "Book Load" : "Not Available"}
                </Button>

                <Dialog open={bidOpen} onOpenChange={setBidOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="open-bid-btn" variant="outline" className="w-full h-12 border-xd-border text-white hover:bg-xd-surface font-semibold">Submit Bid</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-xd-paper border-xd-border text-white">
                    <DialogHeader><DialogTitle>Submit a bid — {load.xd_number}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div><Label className="text-xd-muted">Your rate ($)</Label><Input data-testid="bid-rate" type="number" value={bidRate} onChange={(e) => setBidRate(e.target.value)} className="mt-1.5 bg-xd-surface border-xd-border" /></div>
                      <div><Label className="text-xd-muted">Notes</Label><Textarea data-testid="bid-notes" value={bidNotes} onChange={(e) => setBidNotes(e.target.value)} className="mt-1.5 bg-xd-surface border-xd-border" placeholder="Availability, equipment notes…" /></div>
                    </div>
                    <DialogFooter><Button data-testid="submit-bid-btn" onClick={submitBid} disabled={busy} className="bg-xd-orange hover:bg-xd-orangeH text-white">Submit Bid</Button></DialogFooter>
                  </DialogContent>
                </Dialog>

                <Button data-testid="save-load-detail-btn" variant="ghost" className="w-full text-xd-muted hover:text-white hover:bg-xd-surface" onClick={save}>Save Load</Button>
                <a href="tel:+17135550100"><Button variant="ghost" className="w-full text-xd-muted hover:text-white hover:bg-xd-surface"><Phone size={16} className="mr-2" /> Contact XD</Button></a>
              </div>
              {user?.role === "carrier" && <p className="mt-4 text-xs text-xd-dim text-center">Only XD Verified Carriers can book. Complete verification in your dashboard.</p>}
            </div>

            <AIDispatchPanel loadId={id} onUseInBid={(rate, note) => {
              if (!requireCarrier()) return;
              setBidRate(String(rate));
              setBidNotes(note || "");
              setBidOpen(true);
              toast.success("Counter-offer loaded into your bid");
            }} />
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={16} className="text-xd-dim mt-0.5 shrink-0" />
      <div><p className="text-[10px] uppercase tracking-wide text-xd-dim">{label}</p><p className="text-white font-medium text-sm">{value}</p></div>
    </div>
  );
}
